import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, ExternalLink, ArrowRight, Check } from "lucide-react";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

const GOOGLE_REVIEW_URL = "https://g.page/r/CQWitwW6TlGCEAE/review";

export default function PromoModal({ isOpen, onClose, onRegister }) {
  const [step, setStep] = useState("ask"); // ask, review, register

  useEffect(() => {
    if (isOpen) setStep("ask");
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 350 }}
          className="w-full max-w-[400px] rounded-t-[28px] sm:rounded-[28px] overflow-hidden shadow-2xl relative"
          style={{ backgroundColor: C.bgCard, maxHeight: "90vh" }}
          onClick={e => e.stopPropagation()}
        >
          {/* Drag handle mobile */}
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-10 h-1 rounded-full" style={{ backgroundColor: C.border }} />
          </div>

          {/* Accent bar */}
          <div style={{ height: 4, background: `linear-gradient(90deg, ${C.green}, ${C.orange})` }} />

          {/* Close */}
          <button onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center z-10"
            style={{ backgroundColor: C.bg }}>
            <X size={16} style={{ color: C.textMuted }} />
          </button>

          <div className="px-6 pt-6 pb-8 overflow-y-auto">
            <AnimatePresence mode="wait">

              {/* ═══ STEP 1: Ask about review ═══ */}
              {step === "ask" && (
                <motion.div key="ask"
                  initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                  className="text-center"
                >
                  {/* Matcha illustration */}
                  <div className="mb-4">
                    <span className="text-6xl block">🍵</span>
                  </div>

                  <h2 className="text-[22px] font-bold mb-2"
                    style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                    Gratis Matcha Latte!
                  </h2>
                  <p className="text-sm mb-6" style={{ color: C.textMuted }}>
                    Registriere dich und hol dir einen <strong style={{ color: C.green }}>Gratis Matcha Latte</strong>.
                    Aber zuerst eine kurze Frage:
                  </p>

                  {/* Question */}
                  <div className="p-4 rounded-2xl mb-5" style={{ backgroundColor: C.bg }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Star size={18} style={{ color: C.orange }} fill={C.orange} />
                      <Star size={18} style={{ color: C.orange }} fill={C.orange} />
                      <Star size={18} style={{ color: C.orange }} fill={C.orange} />
                      <Star size={18} style={{ color: C.orange }} fill={C.orange} />
                      <Star size={18} style={{ color: C.orange }} fill={C.orange} />
                    </div>
                    <p className="text-[15px] font-bold" style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                      Hast du schon eine Google-Bewertung fuer uns abgegeben?
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="space-y-3">
                    <motion.button whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setStep("register");
                      }}
                      className="w-full py-4 rounded-2xl font-bold text-white text-[15px] flex items-center justify-center gap-2"
                      style={{ background: `linear-gradient(135deg, ${C.green}, ${C.greenDark || "#1E3D2A"})` }}>
                      <Check size={18} /> Ja, hab ich schon!
                    </motion.button>

                    <motion.button whileTap={{ scale: 0.98 }}
                      onClick={() => setStep("review")}
                      className="w-full py-4 rounded-2xl font-bold text-[15px] flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: C.orange + "12",
                        color: C.orange,
                        border: `2px solid ${C.orange}30`,
                      }}>
                      Nein, noch nicht
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* ═══ STEP 2: Please review first ═══ */}
              {step === "review" && (
                <motion.div key="review"
                  initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                  className="text-center"
                >
                  <div className="mb-4">
                    <span className="text-6xl block">⭐</span>
                  </div>

                  <h2 className="text-[22px] font-bold mb-2"
                    style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                    Hilf uns zu wachsen!
                  </h2>
                  <p className="text-sm mb-5" style={{ color: C.textMuted }}>
                    Deine Bewertung bedeutet uns die Welt! Hinterlasse eine kurze Google-Bewertung
                    und sichere dir danach deinen <strong style={{ color: C.green }}>Gratis Matcha Latte</strong>.
                  </p>

                  {/* Google Review Button */}
                  <motion.a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-4 rounded-2xl font-bold text-white text-[15px] flex items-center justify-center gap-2 mb-4 block"
                    style={{ background: `linear-gradient(135deg, ${C.orange}, ${C.orangeDark || "#C25D3D"})` }}
                  >
                    <ExternalLink size={18} /> Jetzt bewerten auf Google
                  </motion.a>

                  <p className="text-[12px] mb-4" style={{ color: C.textMuted }}>
                    Dauert nur 30 Sekunden - wir schaetzen es sehr!
                  </p>

                  <div className="w-full h-px mb-4" style={{ backgroundColor: C.border }} />

                  <p className="text-[13px] mb-3" style={{ color: C.textMuted }}>
                    Schon bewertet?
                  </p>
                  <motion.button whileTap={{ scale: 0.98 }}
                    onClick={() => setStep("register")}
                    className="w-full py-3.5 rounded-2xl font-bold text-[15px] flex items-center justify-center gap-2"
                    style={{ background: `linear-gradient(135deg, ${C.green}, ${C.greenDark || "#1E3D2A"})`, color: "#fff" }}>
                    <ArrowRight size={18} /> Weiter zur Registrierung
                  </motion.button>
                </motion.div>
              )}

              {/* ═══ STEP 3: Go register ═══ */}
              {step === "register" && (
                <motion.div key="register"
                  initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                  className="text-center"
                >
                  <div className="mb-4">
                    <span className="text-6xl block">🎉</span>
                  </div>

                  <h2 className="text-[22px] font-bold mb-2"
                    style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
                    Super, danke dir!
                  </h2>
                  <p className="text-sm mb-2" style={{ color: C.textMuted }}>
                    Registriere dich jetzt und dein <strong style={{ color: C.green }}>Gratis Matcha Latte</strong> wartet
                    direkt in deinen Coupons auf dich.
                  </p>

                  <div className="p-4 rounded-2xl mb-5 flex items-center gap-3"
                    style={{ backgroundColor: C.green + "10" }}>
                    <span className="text-3xl">🍵</span>
                    <div className="text-left">
                      <p className="text-[14px] font-bold" style={{ color: C.green }}>
                        Gratis Matcha Latte
                      </p>
                      <p className="text-[12px]" style={{ color: C.textMuted }}>
                        14 Tage gültig nach Registrierung
                      </p>
                    </div>
                  </div>

                  <motion.button whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      onClose();
                      // Open auth modal in register mode with google_review flag
                      document.dispatchEvent(new CustomEvent("set-auth-mode", { detail: "register" }));
                      document.dispatchEvent(new CustomEvent("show-auth-modal"));
                      // Store promo flag
                      localStorage.setItem("frankysfarm_promo", "matcha-gratis");
                      localStorage.setItem("frankysfarm_google_review", "true");
                    }}
                    className="w-full py-4 rounded-2xl font-bold text-white text-[15px] flex items-center justify-center gap-2"
                    style={{ background: `linear-gradient(135deg, ${C.green}, ${C.greenDark || "#1E3D2A"})` }}>
                    Jetzt kostenlos registrieren <ArrowRight size={18} />
                  </motion.button>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
